import { Listing } from '@smg-real-estate/hg-rets-schema';
import { getRetryingAxiosInstance } from '@smg-real-estate/hg-search-libs';

import { getAcrHeaders, shouldInjectAcrHeaders } from '@/helpers/get-acr-headers';
import { NewConstructionDataWithOtherListings } from '@/models';
import { NewConstructionStore } from '@/store/types/new-construction-state';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const axiosInstance = getRetryingAxiosInstance({
  timeout: 2000,
  retryConfig: {
    retries: 3,
  },
});

export const useNewConstructionStore = defineStore('newConstruction', (): NewConstructionStore => {
  const newConstructionData = ref<NewConstructionDataWithOtherListings | null | undefined>(null);

  const setNewConstructionData = (
    data: NewConstructionDataWithOtherListings | null | undefined,
  ) => {
    newConstructionData.value = data;
  };

  const handleNewConstructionListings = async ({
    listingId,
    newConstructionData,
  }: {
    listingId: string | null | undefined;
    newConstructionData: NewConstructionDataWithOtherListings | null | undefined;
  }): Promise<void> => {
    try {
      let otherListings: Listing[] | undefined;

      let headers: Record<string, string> = {};
      if (shouldInjectAcrHeaders()) {
        headers = { ...(await getAcrHeaders()) };
      }

      const projectIdentifier = newConstructionData?.projectName;
      const trimmedProjectIdentifier =
        typeof projectIdentifier === 'string' ? projectIdentifier.trim() : undefined;

      if (trimmedProjectIdentifier) {
        otherListings = (
          (await axiosInstance.get(
            `${import.meta.env.VUE_APP_LISTING_API_ENDPOINT}/new-construction-projects/${trimmedProjectIdentifier}`,
            { headers },
          )) as any
        ).data.listings.filter((listing: Listing) => listing.id !== listingId);
      }

      setNewConstructionData(
        otherListings && otherListings.length > 0
          ? ({ ...newConstructionData, otherListings } as NewConstructionDataWithOtherListings)
          : newConstructionData,
      );
    } catch (e) {
      setNewConstructionData(newConstructionData);
    }
  };

  return {
    newConstructionData,
    setNewConstructionData,
    handleNewConstructionListings,
  };
});
